/*
 * Single source of truth for sensitive-data redaction patterns and helpers,
 * shared by the monolith's pino-http config and the Next.js apps' request
 * logging. This lib must stay pure (zero runtime imports) — it is loaded in
 * constrained contexts such as the Next.js instrumentation boot chain.
 */

export const REDACTED_TEXT = '[REDACTED]';

export const ALWAYS_FULLY_REDACTED = [
  'authorization',
  'Authorization',
  'x-api-key',
  'x-ApiKey',
  'x-ClientKey',
  'x-Authorization',
  'x-access-token',
  // Machine credential on service-to-service calls; rename here too if the
  // header ever changes (this lib is import-free, so no single-sourcing).
  'x-internal-api-key',
  'access_token',
  'refresh_token',
  'token',
  'jwt',
] as const;

/*
 * Fully redacted only when the field name matches EXACTLY (case-insensitive).
 * These names are too common as substrings to blocklist broadly: 'code' as a
 * substring would redact zipCode, errorCode, etc. Used for query params
 * (OAuth/Frontegg-style ?code=... callbacks); body fields named 'code' are
 * typically error codes, hence not part of the substring patterns.
 */
export const QUERY_PARAM_EXACT_FULLY_REDACTED = ['code'];

export const SENSITIVE_BODY_FIELD_PATTERNS = [
  'password',
  'ssn',
  'socialSecurityNumber',
  'creditCard',
  'card_number',
  'cvv',
  'dateOfBirth',
  'dob',
  'bankAccount',
  'accountNumber',
  // ACH fields as HealthSherpa's EnrollConnect API names them
  'eft_number',
  'eft_routing',
  'secret',
  'key',
  'email',
];

export const SENSITIVE_FIELD_PATTERNS = [
  ...SENSITIVE_BODY_FIELD_PATTERNS,
  ...ALWAYS_FULLY_REDACTED,
];

// Lowercased once at module load — these checks run on hot logging paths
const SENSITIVE_FIELD_PATTERNS_LOWER = SENSITIVE_FIELD_PATTERNS.map((pattern) =>
  pattern.toLowerCase()
);
const ALWAYS_FULLY_REDACTED_LOWER = ALWAYS_FULLY_REDACTED.map((pattern) =>
  pattern.toLowerCase()
);

/**
 * Checks if a field name matches any sensitive field pattern
 */
export function isSensitiveField(fieldName: string): boolean {
  const lowerFieldName = fieldName.toLowerCase();
  return SENSITIVE_FIELD_PATTERNS_LOWER.some((pattern) =>
    lowerFieldName.includes(pattern)
  );
}

/**
 * Checks if a field should be completely redacted (no partial reveal)
 */
export function isFullyRedactedField(fieldName: string): boolean {
  const lowerFieldName = fieldName.toLowerCase();
  return ALWAYS_FULLY_REDACTED_LOWER.some((pattern) =>
    lowerFieldName.includes(pattern)
  );
}

/**
 * Redacts a single value according to redaction rules: full redaction for
 * secret-bearing fields, first/last character reveal for other sensitive
 * string values longer than 6 characters.
 */
export function redactValue(value: unknown, fieldName: string): unknown {
  if (isFullyRedactedField(fieldName)) {
    return REDACTED_TEXT;
  }

  if (typeof value !== 'string') {
    return REDACTED_TEXT;
  }

  if (value.length <= 6) {
    return REDACTED_TEXT;
  }

  // Partially redact: show first and last character
  return `${value[0]}****${value[value.length - 1]}`;
}

/**
 * Deep redacts sensitive fields in an object
 */
export function redactSensitiveData(data: unknown): unknown {
  if (data === null || data === undefined) {
    return data;
  }

  if (Array.isArray(data)) {
    return data.map((item) => redactSensitiveData(item));
  }

  if (typeof data === 'object') {
    const redacted: Record<string, unknown> = {};
    for (const [key, value] of Object.entries(data)) {
      if (isSensitiveField(key)) {
        redacted[key] = redactValue(value, key);
      } else if (typeof value === 'object' && value !== null) {
        redacted[key] = redactSensitiveData(value);
      } else {
        redacted[key] = value;
      }
    }
    return redacted;
  }

  return data;
}

/**
 * Redacts sensitive fields inside an outbound request body. Axios replaces
 * config.data with the JSON-serialized string before sending — a string
 * redactSensitiveData would pass through untouched — so serialized bodies
 * are parsed first; anything unparseable is fully redacted.
 */
export function redactSerializedBody(body: unknown): unknown {
  if (typeof body !== 'string') {
    return redactSensitiveData(body);
  }

  try {
    return JSON.stringify(redactSensitiveData(JSON.parse(body)));
  } catch {
    return REDACTED_TEXT;
  }
}
